import { useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { logoutUser } from '@/actions/authActions'
import { selectIsReactivatedUser } from '@/selectors/user.selectors'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsReset } from '@/features/amplitude'

import {
  SIGNUP_STEPS,
  SIGNUP_STEPS_2024,
  SIGNUP_STEPS_FREE_TRIAL_2024,
} from './helpers'

import { Alert, Button, Image, Progress } from '@/ui'

// TODO: Migrate to V2
import { selectCanOptIntoPreviousTaxYear } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.selector'

const SignupHeaderLogic = ({ currentStep }: { currentStep: number }) => {
  const [searchParams] = useSearchParams()
  const freeTrialSearchParamValue = searchParams.get('trial')
  const canOptIntoPreviousYearTaxes = useReselector(
    selectCanOptIntoPreviousTaxYear
  )
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const basicPlanSearchParamValue = searchParams.get('subscription')
  const useBasicTypePlan =
    basicPlanSearchParamValue === 'new' || basicPlanSearchParamValue === 'basic'

  const progress = useMemo(() => {
    let steps = SIGNUP_STEPS

    if (freeTrialSearchParamValue) {
      steps = SIGNUP_STEPS_FREE_TRIAL_2024
    } else if (useBasicTypePlan) {
      steps = SIGNUP_STEPS
    } else if (canOptIntoPreviousYearTaxes) {
      steps = SIGNUP_STEPS_2024
    }

    const totalSteps = steps.length

    if (isMobile) {
      return (
        <Progress
          variant="segmented"
          numberOfSegments={totalSteps}
          activeSegment={currentStep + 1}
        />
      )
    }

    return (
      <Progress
        variant="segmented"
        numberOfSegments={steps.length}
        activeSegment={currentStep + 1}
        segmentTitles={steps}
      />
    )
  }, [
    freeTrialSearchParamValue,

    canOptIntoPreviousYearTaxes,
    currentStep,
    isMobile,
    useBasicTypePlan,
  ])

  return progress
}

const DesktopHeader = ({
  loggedIn,
  handleLogoutClick,
  currentStep,
  reactivating,
  hideProgress,
}: {
  loggedIn: boolean
  handleLogoutClick: () => void
  currentStep: number
  reactivating: boolean
  hideProgress?: boolean
}) => {
  return (
    <>
      <div className="flex items-center justify-between pb-0">
        <Image
          src="https://heard-images.s3.us-west-1.amazonaws.com/heard_logo_green_2x.png"
          alt="heard logo"
          width={75}
        />

        {loggedIn && (
          <Button
            className="w-[12.5%] text-right text-brand"
            variant="link"
            onClick={() => handleLogoutClick()}
          >
            Logout
          </Button>
        )}
      </div>

      {!hideProgress && (
        <div className="min-h-fit mt-6">
          <SignupHeaderLogic currentStep={currentStep} />
        </div>
      )}

      {reactivating && (
        <Alert
          className="min-h-fit mt-4"
          title="Welcome back to Heard!"
          description="To reactivate your account, please proceed through the sign up flow."
        />
      )}
    </>
  )
}

const MobileHeader = ({
  loggedIn,
  handleLogoutClick,
  currentStep,
  hideProgress,
}: {
  loggedIn: boolean
  handleLogoutClick: () => void
  currentStep: number
  reactivating: boolean
  hideProgress?: boolean
}) => {
  return (
    <>
      <div className="flex items-center justify-between pb-0">
        <Image
          src="https://heard-images.s3.us-west-1.amazonaws.com/heard_logo_green_2x.png"
          alt="heard logo"
          className="h-5"
        />

        {loggedIn && (
          <Button
            className="float-right"
            variant="link"
            onClick={() => handleLogoutClick()}
          >
            Logout
          </Button>
        )}
      </div>

      {!hideProgress && (
        <div className="min-h-fit mt-6">
          <div className="sticky top-0 bg-white shadow-md px-4 -mx-4 w-full z-10">
            <SignupHeaderLogic currentStep={currentStep} />
          </div>
        </div>
      )}
    </>
  )
}

export const SignupHeader = ({
  currentStep,
  hideProgress,
}: {
  currentStep?: number
  hideProgress?: boolean
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const resetAnalytics = useAnalyticsReset()
  const reactivatingUser = useReselector(selectIsReactivatedUser)
  /* 
    step is 0 if on account creation page
  */
  const loggedIn = currentStep !== 0

  const handleLogoutClick = useCallback(async () => {
    resetAnalytics()
    await logoutUser()(dispatch)
    localStorage.removeItem('targetLink')
    navigate('/login')
  }, [resetAnalytics, navigate, dispatch])

  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  return isMobile ? (
    <MobileHeader
      currentStep={currentStep || 0}
      loggedIn={loggedIn}
      handleLogoutClick={handleLogoutClick}
      reactivating={reactivatingUser}
      hideProgress={hideProgress}
    />
  ) : (
    <DesktopHeader
      loggedIn={loggedIn}
      handleLogoutClick={handleLogoutClick}
      currentStep={currentStep || 0}
      reactivating={reactivatingUser}
      hideProgress={hideProgress}
    />
  )
}
